import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "i18n";

import { faqCategories } from "./data";
import "./style.css";

const MOBILE_TOPIC_MQ = "(max-width: 900px)";

const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

function AnswerText({ text }) {
  const parts = String(text).split(URL_RE);

  return (
    <p className="faq-answer">
      {parts.map((part, index) => {
        if (!/^(https?:\/\/|www\.)/i.test(part)) return part;

        const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
        return (
          <a
            key={`${part}-${index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part.replace(/^https?:\/\//i, "")}
          </a>
        );
      })}
    </p>
  );
}

function FAQ() {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredCategories = useMemo(() => {
    const query = search.toLowerCase().trim();
    if (!query) return faqCategories;

    return faqCategories
      .map((category) => ({
        ...category,
        questions: category.questions.filter(
          (item) =>
            item.q.toLowerCase().includes(query) ||
            item.a.toLowerCase().includes(query),
        ),
      }))
      .filter((category) => category.questions.length > 0);
  }, [search]);

  const safeIndex =
    activeCategory < filteredCategories.length ? activeCategory : 0;
  const currentCategory = filteredCategories[safeIndex];
  const questionCount = currentCategory?.questions.length || 0;
  const totalMatches = filteredCategories.reduce(
    (sum, category) => sum + category.questions.length,
    0,
  );

  const handleSearch = (value) => {
    setSearch(value);
    setActiveCategory(0);
    setExpanded(null);
  };

  const handleCategoryChange = (index) => {
    setActiveCategory(index);
    setExpanded(null);

    if (!window.matchMedia(MOBILE_TOPIC_MQ).matches) return;

    requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const toggleQuestion = (questionIndex) => {
    setExpanded((current) =>
      current === questionIndex ? null : questionIndex,
    );
  };

  return (
    <div className="faq-page">
      <section className="faq-hero">
        <div className="faq-hero__glow" aria-hidden="true" />
        <div className="container faq-hero__content">
          <p className="faq-hero__eyebrow">{t("faq.eyebrow")}</p>
          <h1 className="faq-hero__title">{t("faq.title")}</h1>
          <p className="faq-hero__subtitle">{t("faq.description")}</p>
        </div>
      </section>

      <div className="container faq-shell">
        <label className="faq-search">
          <span className="visually-hidden">{t("faq.searchPlaceholder")}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" />
            <path d="M16.2 16.2 21 21" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(event) => handleSearch(event.target.value)}
            placeholder={t("faq.searchPlaceholder")}
          />
        </label>

        {filteredCategories.length > 0 ? (
          <div className="faq-layout">
            <aside className="faq-topics">
              <p className="faq-topics__label">{t("faq.browseTopics")}</p>
              <nav className="faq-topics__list" aria-label={t("faq.browseTopics")}>
                {filteredCategories.map((category, index) => {
                  const active = safeIndex === index;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      className={`faq-topic${active ? " is-active" : ""}`}
                      aria-current={active ? "true" : undefined}
                      onClick={() => handleCategoryChange(index)}
                    >
                      <span className="faq-topic__index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="faq-topic__copy">
                        <span className="faq-topic__title">{category.title}</span>
                        <span className="faq-topic__count">
                          {category.questions.length === 1
                            ? t("faq.questionCountOne")
                            : t("faq.questionsCount", {
                                count: category.questions.length,
                              })}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            <section
              ref={panelRef}
              id="faq-questions"
              className="faq-panel"
              aria-live="polite"
            >
              <header className="faq-panel__head">
                <div>
                  <p className="faq-kicker">{t("faq.browseTopics")}</p>
                  <h2>{currentCategory.title}</h2>
                </div>
                <p className="faq-panel__meta">
                  {search.trim()
                    ? t("faq.resultsCount", { count: totalMatches })
                    : questionCount === 1
                      ? t("faq.questionCountOne")
                      : t("faq.questionsCount", { count: questionCount })}
                </p>
              </header>

              <div className="faq-list">
                {currentCategory.questions.map((faq, questionIndex) => {
                  const isOpen = expanded === questionIndex;
                  return (
                    <article
                      key={`${currentCategory.id}-${questionIndex}`}
                      className={`faq-item${isOpen ? " is-open" : ""}`}
                    >
                      <h3>
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          onClick={() => toggleQuestion(questionIndex)}
                        >
                          <span>{faq.q}</span>
                          <i className="faq-item__icon" aria-hidden="true" />
                        </button>
                      </h3>
                      <div className="faq-item__body">
                        <div>
                          <AnswerText text={faq.a} />
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          </div>
        ) : (
          <div className="faq-empty">
            <h2>{t("faq.emptyTitle")}</h2>
            <p>{t("faq.emptyDescription")}</p>
          </div>
        )}

        <section className="faq-cta">
          <p className="faq-kicker">{t("faq.ctaEyebrow")}</p>
          <h2>{t("faq.ctaTitle")}</h2>
          <p>{t("faq.ctaDescription")}</p>
          <Link to="/contact-us">{t("faq.ctaAction")}</Link>
        </section>
      </div>
    </div>
  );
}

export default FAQ;
